import React from 'react';

interface InfoBoxProps {
  headline: string;
  highlightText?: string;
  body: string;
  ctaText?: string;
  ctaAction?: () => void;
  ctaHref?: string;
  imageUrl: string;
  imageAlt: string;
  imagePosition?: 'left' | 'right';
  className?: string;
}

function InfoBox({
  headline,
  highlightText,
  body,
  ctaText,
  ctaAction,
  ctaHref,
  imageUrl,
  imageAlt,
  imagePosition = 'right',
  className = '',
}: InfoBoxProps) {
  const isImageLeft = imagePosition === 'left';

  return (
    <section
      className={`au-bg-[#161616] au-text-white au-py-12 au-px-6 ${className}`}
    >
      <div className="au-max-w-6xl au-mx-auto au-border-2 au-rounded-lg au-border au-border-[#FEFF09]">
        <div
          className={`au-flex ${
            isImageLeft
              ? 'au-flex-col-reverse md:au-flex-row-reverse lg:au-flex-row-reverse'
              : 'au-flex-col md:au-flex-row'
          } au-items-stretch au-min-h-c300 md:au-min-h-c350 lg:au-min-h-c400`}
        >
          <div className="au-w-full md:au-flex-[0.65] lg:au-flex-[0.6] au-space-y-6 au-p-8">
            <h1 className="au-text-xl md:au-text-2xl lg:au-text-3xl au-font-bold au-leading-tight">
              {headline}
              {highlightText && (
                <span className="au-block au-text-[#FEFF09]">
                  {highlightText}
                </span>
              )}
            </h1>

            <p className="au-text-lg au-text-gray-300 au-leading-relaxed au-max-w-2xl">
              {body}
            </p>

            {ctaText &&
              (ctaHref ? (
                <a
                  href={ctaHref}
                  className="au-bg-[#FEFF09] au-inline-block au-text-black au-font-semibold au-px-8 au-py-3 au-rounded-lg au-transition-colors au-duration-200 au-shadow-lg hover:au-shadow-xl au-transform hover:au-scale-105 au-no-underline"
                >
                  {ctaText}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={ctaAction}
                  className="au-bg-[#FEFF09] au-text-black au-font-semibold au-px-8 au-py-3 au-rounded-lg au-transition-colors au-duration-200 au-shadow-lg hover:au-shadow-xl au-transform hover:au-scale-105"
                >
                  {ctaText}
                </button>
              ))}
          </div>

          <div className="au-w-full md:au-flex-[0.35] lg:au-flex-[0.4]">
            <img
              src={imageUrl}
              alt={imageAlt}
              className={`au-w-full au-h-full au-object-cover au-max-h-c300 md:au-max-h-none ${
                isImageLeft
                  ? 'au-rounded-l-lg md:au-rounded-r-none lg:au-rounded-r-none'
                  : 'au-rounded-r-lg md:au-rounded-l-none lg:au-rounded-l-none'
              }`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

InfoBox.attributes = {
  headline: {
    type: 'string',
  },
  highlightText: {
    type: 'string',
  },
  body: {
    type: 'string',
  },
  ctaText: {
    type: 'string',
  },
  ctaHref: {
    type: 'string',
  },
  imageUrl: {
    type: 'string',
  },
  imageAlt: {
    type: 'string',
  },
  imagePosition: {
    type: 'string',
    options: ['left', 'right'],
  },
  className: {
    type: 'string',
  },
};

export default InfoBox;
